/**
 * FormModeContent
 *
 * Form-mode body of the composite field sidebar, rendered by
 * `CompositeFieldTypeContent` when there is no cell proxy or the sidebar was
 * not opened in table mode. Holds the composite value in local state seeded
 * from `getCurrentValue()` and pushes each edit through to the `onChange`
 * captured at open time; it does not subscribe to any cell proxy.
 */

import { FC, useCallback, useState } from 'react';
import type { ArCellValue } from '#types';
import type { CompositeFieldValue, UIElement } from '#types/System.types';
import { CompositeFieldProvider } from '../CompositeFieldProvider';
import LayoutUI from '../../rendering/LayoutUI';
import {
  getOnChange,
  getCurrentValue,
  getFormulaSequence,
} from '../compositeFieldSidebarState';

type FormModeContentProps = {
  compositeFieldTypeId: string;
  fieldId: string;
  isFormulaColumn: boolean;
  onChange: ReturnType<typeof getOnChange>;
  structure: UIElement | undefined;
};

/** Form mode: local state, direct pass-through. */
export const FormModeContent: FC<FormModeContentProps> = ({
  compositeFieldTypeId,
  fieldId,
  isFormulaColumn,
  onChange,
  structure,
}) => {
  const [localValue, setLocalValue] = useState<CompositeFieldValue | undefined>(getCurrentValue);
  const formulaSequence = getFormulaSequence();

  const handleParamsChange = useCallback((raw: ArCellValue) => {
    if (!onChange) return;
    const nextParams = raw as CompositeFieldValue;
    setLocalValue(nextParams);
    onChange(nextParams as ArCellValue);
  }, [onChange]);

  if (!structure) return null;

  return (
    <CompositeFieldProvider
      compositeFieldId={fieldId}
      compositeFieldTypeId={compositeFieldTypeId}
      value={localValue}
      isFormulaColumn={isFormulaColumn}
      formulaSequence={formulaSequence}
      onChange={handleParamsChange}
    >
      <LayoutUI uiElement={structure} />
    </CompositeFieldProvider>
  );
};
